<template>
  <div class="p-[1.5rem] shadow-[0px_0px_8px_0px_#3A3A3A1F] rounded-[8px]">
    <article-header
      :total="totalArticles"
      :search="search"
      :status="status"
      :has-selected="hasSelectedArticles"
      @search="handleSearch"
      @status-change="handleChangeStatus"
      @add="openAdd"
      @bulk-action="handleBulkAction"
    />

    <BOverlay
      :show="loading"
      spinner-variant="primary"
      variant="light"
      blur="0"
      opacity=".5"
      rounded="sm"
    >
      <article-list
        ref="artikelList"
        :groups="groups"
        @edit="openEdit"
        @delete="openDelete"
        @load-more="handleLoadMore"
        @selection-change="selectedCount = $event"
      />
    </BOverlay>

    <ModalTambahArtikel
      ref="modalTambahArtikel"
      :selected="selectedItem"
      :is-edit="isEdit"
      @hidden="resetForm"
      @saved="getListData"
    />
    <article-delete-dialog
      ref="articleDeleteDialog"
      @deleted="getListData"
      @close="onDeleteDialogClosed"
    />
  </div>
</template>

<script>
import moment from 'moment'
import ArticleHeader from './ArticleHeader.vue'
import ArticleList from './ArticleList.vue'
import ModalTambahArtikel from './ModalTambahArtikel.vue'
import ArticleDeleteDialog from './ArticleDeleteDialog.vue'

export default {
  name: 'TabArtikel',
  components: {
    ArticleHeader,
    ArticleList,
    ModalTambahArtikel,
    ArticleDeleteDialog,
  },
  data() {
    const cores = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4
    const memory = (typeof navigator !== 'undefined' && navigator.deviceMemory) || 8
    const isLowEndDevice = cores <= 2 || memory <= 2

    return {
      loading: false,
      groups: [],
      search: this.$route.query.search || '',
      status: this.$route.query.status || null,
      selectedItem: null,
      isEdit: false,
      selectedCount: 0,
      totalArticles: 0,
      isLowEndDevice,
    }
  },
  computed: {
    hasSelectedArticles() {
      return this.selectedCount > 1
    },
    totalLoadedArticles() {
      return this.groups.reduce((acc, g) => acc + (g.articles?.length || 0), 0)
    },
  },
  mounted() {
    this.getListData()
  },
  methods: {
    handleSearch(keyword) {
      this.search = keyword
      this.updateRouteQuery()
      this.getListData()
    },
    handleChangeStatus(newValue) {
      this.status = newValue
      this.updateRouteQuery()
      this.getListData()
    },
    /**
     * Keeps the URL query string in sync with the current search and status filters.
     * Uses router.replace to avoid polluting the browser history stack.
     */
    updateRouteQuery() {
      const query = { ...this.$route.query }

      if (this.search) query.search = this.search
      else delete query.search

      if (this.status) query.status = this.status
      else delete query.status

      if (
        this.$route.query.search !== query.search
        || this.$route.query.status !== query.status
      ) {
        this.$router.replace({ query }).catch(() => {})
      }
    },
    /**
     * Handles a bulk action dispatched from the ArticleHeader toolbar.
     * Collects all selected entries, sets their loading state, then performs
     * the appropriate API call based on the action type.
     * @param {{ action: 'status'|'category'|'delete', payload: * }} param0
     */
    async handleBulkAction({ action, payload }) {
      const selectedEntries = this.$refs.artikelList?.getSelectedItems() || []

      if (selectedEntries.length === 0) return

      // Gunakan ID asli (number) dari item, bukan key dari object entries (string)
      const selectedIds = selectedEntries.map(([, item]) => item.id)

      selectedEntries.forEach(([, { setLoading }]) => setLoading?.(true, action))

      try {
        switch (action) {
          case 'status': {
            await this.bulkUpdateStatus(selectedIds, payload)

            // Turn off loading state and update item status/date using their original indices
            selectedEntries.forEach(([, { setLoading }]) => {
              if (setLoading) setLoading(false, action, payload)
            })

            this.$toast_success({ message: `Status ${selectedIds.length} artikel berhasil diubah` })

            // Refetch dari backend jika berpotensi overload, sebaliknya lakukan reorder manual di frontend
            if (this.isSystemOverload(selectedIds.length)) {
              await this.getListData()
            } else {
              const selectedSet = new Set(selectedIds)
              this.groups.forEach((group, index) => {
                if (!group.articles || group.articles.length === 0) return
                const moved = []
                const remaining = []
                group.articles.forEach(article => {
                  if (selectedSet.has(article.id)) {
                    moved.push(article)
                  } else {
                    remaining.push(article)
                  }
                })
                if (moved.length > 0) {
                  this.groups[index].articles = [...moved, ...remaining]
                }
              })
            }

            if (this.$refs.artikelList) this.$refs.artikelList.resetSelection()
            break
          }
          case 'category':
            await this.bulkUpdateCategory(selectedIds, payload)
            selectedEntries.forEach(([_, { setLoading }]) => {
              if (setLoading) setLoading(false, action)
            })
            this.$toast_success({ message: `Berhasil memindahkan ${selectedIds.length} artikel ke kategori ${payload?.name || 'Tanpa Kategori'}` })
            await this.getListData()
            if (this.$refs.artikelList) this.$refs.artikelList.resetSelection()
            break
          default: {
            this.$refs.articleDeleteDialog.show({
              item: { title: selectedEntries[0][1].title, count: selectedEntries.length },
              onSubmit: async () => {
                await this.bulkDelete(selectedIds)
                selectedEntries.forEach(([_, { setLoading }]) => setLoading?.(false))
                this.$toast_success({ message: `${selectedIds.length} artikel berhasil dihapus permanen` })
                await this.getListData()
                if (this.$refs.artikelList) this.$refs.artikelList.resetSelection()
              },
              onCancel: () => {
                selectedEntries.forEach(([_, { setLoading }]) => setLoading?.(false))
              },
            })
            break
          }
        }
      } catch (error) {
        selectedEntries.forEach(([_, { setLoading }]) => setLoading?.(false))
        this.$toast_error({ message: error.message || `Gagal mengubah ${action} artikel` })
      }
    },
    /**
     * Fast O(1) check using cached device specs and computed totalLoadedArticles.
     * @param {number} selectedCount
     * @returns {boolean}
     */
    isSystemOverload(selectedCount) {
      if (selectedCount > 30) return true
      if (this.isLowEndDevice && selectedCount > 15) return true
      if (this.totalLoadedArticles > 100 && selectedCount > 10) return true
      return false
    },
    bulkUpdateStatus(ids, status) {
      return this.$http_new.patch('/live-chat/api/v1/articles/bulk/status', {
        ids,
        status,
      }).catch(error => {
        this.$toast_error({ message: error.response?.data?.message || `Gagal mengubah status ${ids.length} artikel, silahkan coba lagi` })
        throw error
      })
    },
    async bulkUpdateCategory(ids, payload) {
      return this.$http_new.patch('/live-chat/api/v1/articles/bulk/category', {
        ids,
        category_id: payload ? payload.id : null,
      }).catch(error => {
        this.$toast_error({ message: error.response?.data?.message || `Gagal mengubah kategori ${ids.length} artikel, silahkan coba lagi` })
        throw error
      })
    },
    async bulkDelete(ids) {
      return this.$http_new.delete('/live-chat/api/v1/articles/bulk', {
        data: { ids },
      }).catch(error => {
        this.$toast_error({ message: error.response?.data?.message || `Gagal menghapus ${ids.length} artikel, silahkan coba lagi` })
        throw error
      })
    },
    /**
     * Fetches the article list from the API and populates `groups`.
     * Supports both full reloads and incremental "load more" appends.
     * @param {Object} [options={}]
     * @param {Function} [options.set_loading] - External loading setter (for load-more; skips the overlay)
     * @param {number} [options.limit] - Page size override
     * @param {number} [options.category_id] - Fetch articles for a specific category only
     * @param {number} [options.last_id] - Cursor for the next page (ID of the last article in the current page)
     * @param {number} [options.group_index] - When set, appends results into groups[group_index] instead of replacing
     */
    async getListData(options = {}) {
      const setLoading = options.set_loading || (v => { this.loading = v })
      setLoading(true)
      try {
        const params = {
          limit: options.limit || 5,
        }
        if (this.search) params.search = this.search
        if (this.status) params.status = this.status
        if (options.category_id) params.category_id = options.category_id
        if (options.last_id) params.last_id = options.last_id

        const { data } = await this.$http_new.get('/live-chat/api/v1/articles', { params })

        const { groups, total_articles: totalArticles } = data?.data || {}

        this.totalArticles = totalArticles

        if (options.group_index !== undefined) {
          if (!groups || !groups[0]?.articles) return
          const group = this.groups[options.group_index]
          if (!group) return
          this.$set(group, 'articles', [
            ...group.articles,
            ...groups[0].articles,
          ])
          this.$set(group, 'has_more', groups[0].has_more)
          return
        }

        this.groups = groups || []
      } catch (error) {
        this.$toast_error({ message: 'Gagal memuat list artikel' })
      } finally {
        setLoading(false)
      }
    },
    openAdd() {
      this.isEdit = false
      this.selectedItem = null
      this.$refs.modalTambahArtikel.show()
    },
    openEdit(item) {
      this.isEdit = true
      this.selectedItem = item
      this.$refs.modalTambahArtikel.show()
    },
    openDelete(payload) {
      this.$refs.articleDeleteDialog.show({
        item: payload.item,
        onSubmit: () => this.deleteArticle(payload),
      })
    },
    resetForm() {
      this.isEdit = false
      this.selectedItem = null
    },
    /**
     * Deletes a single article and removes it from the local groups array.
     * Decrements the group's article counter on success.
     * @param {{ item: { id: number }, index: number, groupIndex: number }} param0
     */
    async deleteArticle({ item, index, groupIndex }) {
      try {
        await this.$http_new.delete(`/live-chat/api/v1/articles/${item.id}`)
        this.$toast_success({ message: 'Artikel berhasil dihapus' })

        if (groupIndex !== undefined && groupIndex !== -1) {
          this.groups[groupIndex].articles.splice(index, 1)
          if (this.groups[groupIndex].total > 0) this.groups[groupIndex].total -= 1
        }
      } catch (error) {
        this.$toast_error({ message: error.message || 'Gagal menghapus artikel' })
        throw error
      }
    },
    /**
     * Passes load-more options to getListData to append the next page
     * of articles into the corresponding group.
     * @param {Object} options - Same shape as getListData options
     */
    async handleLoadMore(options) {
      this.getListData(options)
    },
    onDeleteDialogClosed() {
      const selectedEntries = this.$refs.artikelList?.getSelectedItems() || []
      selectedEntries.forEach(([_, { setLoading }]) => setLoading?.(false))
    },
  },
}
</script>
